import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ArrowRightLeft, Loader2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useHasPerm, useIsSuperAdmin } from "@/lib/placement";

const BASE = `${process.env.REACT_APP_BACKEND_URL}/api/people`;
const h = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem("nex_token")}` } });
const err = (e) => { const d = e?.response?.data?.detail; return typeof d === "string" ? d : Array.isArray(d) ? d.map((x) => x.msg).join(" ") : "Something went wrong"; };
const LABEL = { staff: "Staff", partner: "Partner", resident: "Resident" };
const input = "w-full px-2 py-1.5 bg-black/40 border border-white/15 rounded-sm text-sm text-white focus:outline-none focus:border-[#d4a017]/60";

function MoveForm({ kind, id, onDone }) {
  const [d, setD] = useState(null);
  const [f, setF] = useState({ to_kind: "", email: "", phone: "", temp_password: "", pin: "", role_id: "", partner_type: "" });
  const [busy, setBusy] = useState(false);
  React.useEffect(() => {
    Promise.all([axios.get(`${BASE}/${kind}/${id}/links`, h()), axios.get(`${BASE}/move-options`, h())]).then(([l, o]) => {
      setD({ ...l.data, ...o.data });
      const left = ["staff", "partner", "resident"].filter((k) => k !== kind && !l.data.links.some((x) => x.kind === k));
      setF((x) => ({ ...x, to_kind: left[0] || "", email: l.data.person.email || "", phone: l.data.person.phone || "" }));
    }).catch((e) => toast.error(err(e)));
  }, [kind, id]);
  if (!d) return <Loader2 className="h-4 w-4 animate-spin text-white/50" />;
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const targets = ["staff", "partner", "resident"].filter((k) => k !== kind);
  const taken = (k) => d.links.some((x) => x.kind === k);
  const roles = d.roles.filter((r) => r.audience === f.to_kind);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const r = await axios.post(`${BASE}/move`, { ...f, from_kind: kind, from_id: id, role_id: f.role_id || null, partner_type: f.partner_type || null }, h());
      toast.success(`${r.data.name} added as ${LABEL[r.data.kind]} · both records stay linked`);
      onDone?.();
    } catch (x) { toast.error(err(x)); } finally { setBusy(false); }
  };
  return (
    <form onSubmit={submit} className="space-y-3" data-testid={`move-form-${id}`}>
      <p className="text-sm text-white/70">{d.person.first_name} {d.person.last_name} keeps their {LABEL[kind].toLowerCase()} record. A new linked record is created.</p>
      {d.links.length > 0 && <p className="text-xs text-white/50" data-testid="move-links">Already linked: {d.links.map((x) => `${LABEL[x.kind]} (${x.name})`).join(", ")}</p>}
      <div className="flex gap-2">{targets.map((k) => (
        <button key={k} type="button" disabled={taken(k)} onClick={() => setF({ ...f, to_kind: k, role_id: "" })} aria-pressed={f.to_kind === k} data-testid={`move-to-${k}`}
          className={`px-3 py-1.5 border rounded-sm text-xs disabled:opacity-30 ${f.to_kind === k ? "border-[#d4a017] text-[#d4a017] bg-[#d4a017]/10" : "border-white/15 text-white/70"}`}>{LABEL[k]}{taken(k) ? " · linked" : ""}</button>))}</div>
      {f.to_kind && f.to_kind !== "resident" && <>
        <input value={f.email} onChange={set("email")} placeholder="Email (their sign-in)" data-testid="move-email" className={input} />
        <p className="text-xs text-white/50" data-testid="move-pin-note">They sign in with this email: on the sign-in page they tap "Get a code by email" and choose their own 6-digit PIN (or use Google).</p>
      </>}
      {f.to_kind === "resident" && <>
        <input value={f.phone} onChange={set("phone")} placeholder="Mobile number (their PACT Life Pass sign-in)" data-testid="move-phone" className={input} />
        <input value={f.pin} onChange={set("pin")} inputMode="numeric" maxLength={6} placeholder="Temporary 6-digit PACT Life Pass PIN" data-testid="move-pin" className={input} />
      </>}
      {f.to_kind === "partner" && (
        <select value={f.partner_type} onChange={set("partner_type")} data-testid="move-partner-type" className={input}>
          <option value="">Partner type…</option>{d.partner_types.map((t) => <option key={t} value={t}>{t.replace(/_/g, " ")}</option>)}
        </select>)}
      {f.to_kind && f.to_kind !== "resident" && (
        <select value={f.role_id} onChange={set("role_id")} data-testid="move-role" className={input}>
          <option value="">{f.to_kind === "staff" ? "No role (set permissions later)" : "No role (partner type defaults)"}</option>
          {roles.map((r) => <option key={r.id} value={r.id}>{r.title}</option>)}
        </select>)}
      <button type="submit" disabled={busy || !f.to_kind} data-testid="move-submit" className="inline-flex items-center gap-2 px-3 py-1.5 border border-[#d4a017]/60 text-[#d4a017] rounded-sm text-sm disabled:opacity-40">
        {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <ArrowRightLeft className="h-3.5 w-3.5" />} Add as {LABEL[f.to_kind] || "…"}</button>
    </form>
  );
}

/** "Move to…" on a staff, partner or resident record (super admins or roles with 'Move people'). */
export function MovePersonButton({ kind, id, onDone, className = "" }) {
  const can = useHasPerm("move_people");
  const [open, setOpen] = useState(false);
  if (!can) return null;
  return (
    <>
      <button type="button" onClick={() => setOpen(true)} data-testid={`move-person-${id}`}
        className={`inline-flex items-center gap-1 px-2 py-1 border border-white/15 rounded-sm text-[11px] text-white/70 hover:text-[#d4a017] hover:border-[#d4a017]/50 ${className}`}>
        <ArrowRightLeft size={12} /> Move to…</button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-[#0b1220] border-white/10 text-white max-w-md" data-testid={`move-dialog-${id}`}>
          <DialogHeader><DialogTitle>Add as another type</DialogTitle></DialogHeader>
          {open && <MoveForm kind={kind} id={id} onDone={() => { setOpen(false); onDone?.(); }} />}
        </DialogContent>
      </Dialog>
    </>
  );
}

/** SA: extra desktop cards for one partner, on top of what their partner role gives them. */
export function PartnerCardsButton({ partner, onDone }) {
  const can = useIsSuperAdmin();
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  React.useEffect(() => { if (open && !items.length) axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/roles`, h()).then((r) => setItems(r.data.cards)).catch((e) => toast.error(err(e))); }, [open]); // eslint-disable-line react-hooks/exhaustive-deps
  const [sel, setSel] = useState(partner.extra_cards || []);
  if (!can) return null;
  const save = () => axios.patch(`${process.env.REACT_APP_BACKEND_URL}/api/roles/partners/${partner.id}/cards`, { cards: sel }, h())
    .then(() => { toast.success("Extra cards saved — they show next time the partner signs in"); setOpen(false); onDone?.(); }).catch((e) => toast.error(err(e)));
  return (
    <>
      <button type="button" onClick={() => { setSel(partner.extra_cards || []); setOpen(true); }} data-testid={`partner-cards-${partner.id}`}
        className="inline-flex items-center gap-1 px-2 py-1 border border-white/15 rounded-sm text-[11px] text-white/70 hover:text-[#d4a017] mr-2">Cards{(partner.extra_cards || []).length ? ` +${partner.extra_cards.length}` : ""}</button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-[#0b1220] border-white/10 text-white max-w-lg max-h-[80vh] overflow-y-auto" data-testid={`partner-cards-dialog-${partner.id}`}>
          <DialogHeader><DialogTitle>Extra cards for {partner.first_name}</DialogTitle></DialogHeader>
          <p className="text-xs text-white/60">These are added to what their partner role already shows. They still only see their assigned residents.</p>
          <div className="flex flex-wrap gap-1.5">{items.map((c) => (
            <button key={c.id} type="button" onClick={() => setSel((s) => (s.includes(c.id) ? s.filter((x) => x !== c.id) : [...s, c.id]))} aria-pressed={sel.includes(c.id)}
              data-testid={`partner-card-${partner.id}-${c.id}`} className={`px-2 py-1 border rounded-sm text-[11px] ${sel.includes(c.id) ? "border-[#d4a017] text-[#d4a017] bg-[#d4a017]/10" : "border-white/10 text-white/60"}`}>{c.label}</button>))}</div>
          <button type="button" onClick={save} data-testid={`partner-cards-save-${partner.id}`} className="px-3 py-1.5 border border-[#d4a017]/60 text-[#d4a017] rounded-sm text-sm">Save {sel.length} extra card(s)</button>
        </DialogContent>
      </Dialog>
    </>
  );
}
